import { ButtonHTMLAttributes, MouseEvent, useCallback } from 'react';
import styled, { css } from 'styled-components';
import FormattedNumber, {
  NumberFormatProps,
  NumberFormatValues,
  SourceInfo,
} from 'react-number-format';
import { noop } from 'lodash';
import { AggressAction } from '@paradigm/features/src/unified-rfqs/ui/one-click-trade/aggress-action-context';
import { formatProductQty } from '@paradigm/features/src/unified-rfqs/ui/decimal-format';
import {
  Report,
  Status,
} from '@paradigm/features/src/unified-rfqs/ui/one-click-trade/types';
import { FailedResp, SuccessResp } from '@paradigm/api';

import { toast } from '#/components/UnifiedRfqs/toast';
import LoadingSpinner from '@paradigm/design-system/src/components/LoadingSpinner';
import { useEscapeListener } from '@paradigm/utils/src/hooks';

import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { ActionButton } from '#/components/UnifiedRfqs/components/ActionButton';
import ConfirmButton from '#/components/UnifiedRfqs/one-click-trade/ConfirmButton';
import PriceButtonWithMark from '#/components/UnifiedRfqs/one-click-trade/PriceButtonWithMark';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import useFormattedPrice from '@paradigm/features/src/unified-rfqs/ui/hooks/useFormattedPrice';
import { useRfq } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import {
  Side,
  getOppositeSide,
} from '@paradigm/features/src/unified-rfqs/entities/shared';
import { useUMPlatformCreationDisabled } from '@paradigm/features/src/platform-state/contexts/providers';
import { useDebouncedTrackAction } from '@paradigm/features/src/analytics';
import type { RfqMarkPrice } from '@paradigm/features/src/unified-rfqs/ui/rfq-websocket/mark-price-context';

export interface SubmitCallback {
  (resp: SuccessResp<Report> | FailedResp): void;
}

interface Props {
  readonly rfqId: string | number;
  readonly onChangeQuantity?: (quantity: string) => void;
  readonly onSubmit: (cb: SubmitCallback) => void;
  readonly resetConfirmState: () => void;
  readonly quantity: string;
  readonly quantityDecimals: number;
  readonly isButtonEnabled: boolean;
  readonly isFieldEnabled: boolean;
  readonly isFieldVisible?: boolean;
  readonly isOB?: boolean;
  readonly status: Status;
  readonly price: string | undefined;
  readonly isMarkOrBetter: RfqMarkPrice['isMarkOrBetter'];
  readonly productCode?: string;
}

export function AggressBuyField({
  rfqId,
  status,
  quantity,
  quantityDecimals,
  onChangeQuantity = noop,
  isButtonEnabled,
  isFieldEnabled,
  isFieldVisible = true,
  onSubmit,
  price,
  resetConfirmState,
  isMarkOrBetter,
  productCode,
  isOB = false,
}: Props) {
  const rfq = useRfq();
  useEscapeListener(resetConfirmState);
  const isPlatformNotAvailable = useUMPlatformCreationDisabled();
  const onClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (!isPlatformNotAvailable) onSubmit(onQuoteSubmitted);
    },
    [isPlatformNotAvailable, onSubmit],
  );
  const isMobile = useIsMobile();
  const formattedPrice = useFormattedPrice({
    price,
    /**
     * When submitting an agress buy (crossing an ask), we should pass the opposite side
     * to format price as the MAKER.
     */
    side: getOppositeSide('BUY'),
    productCode,
    rfqType: rfq.type,
    description: rfq.description,
  });
  return (
    <RelativeContainer>
      <Container side="BUY" aria-label="aggress buy field" status={status}>
        {isMobile && isOB && !isConfirmState(status) && <span />}
        <ConfirmableButton
          onClick={onClick}
          status={status}
          disabled={!isButtonEnabled || isPlatformNotAvailable}
          side="BUY"
          data-testid={rfqId}
          aria-label="rfq_one_click_buy"
          rfqId={rfqId}
        />
        {isMobile && <span />}
        {price != null && (
          <PriceButtonWithMark
            isAggressPrice
            isMarkOrBetter={isMarkOrBetter}
            disabled
            isLockedQuote
            price={price}
            side="BUY"
            productCode={productCode}
            formattedPrice={formattedPrice}
          />
        )}
        <QuantityField
          side="BUY"
          value={quantity}
          status={status}
          decimalScale={quantityDecimals}
          onChange={onChangeQuantity}
          disabled={!isFieldEnabled || isPlatformNotAvailable}
          data-testid={rfqId}
          aria-label="rfq_one_click_qty_buy"
          productCode={productCode}
          isVisible={isFieldVisible}
        />
      </Container>
    </RelativeContainer>
  );
}

export function AggressSellField({
  status,
  quantity,
  quantityDecimals,
  onChangeQuantity = noop,
  onSubmit,
  isButtonEnabled,
  isFieldEnabled,
  isFieldVisible = true,
  price,
  resetConfirmState,
  rfqId,
  isMarkOrBetter,
  productCode,
  isOB = false,
}: Props) {
  useEscapeListener(resetConfirmState);
  const rfq = useRfq();
  const isPlatformNotAvailable = useUMPlatformCreationDisabled();
  const disabled = isPlatformNotAvailable || !isFieldEnabled;
  const onClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (!isPlatformNotAvailable) onSubmit(onQuoteSubmitted);
    },
    [isPlatformNotAvailable, onSubmit],
  );
  const isMobile = useIsMobile();
  const formattedPrice = useFormattedPrice({
    price,
    /**
     * When submitting an agress sell (crossing a bid), we should pass the opposite side
     * to format price as the MAKER.
     */
    side: getOppositeSide('SELL'),
    productCode,
    rfqType: rfq.type,
    description: rfq.description,
  });

  return (
    <RelativeContainer>
      <Container side="SELL" status={status} aria-label="aggress sell field">
        <QuantityField
          side="SELL"
          value={quantity}
          status={status}
          decimalScale={quantityDecimals}
          onChange={onChangeQuantity}
          disabled={disabled}
          data-testid={rfqId}
          aria-label="rfq_one_click_qty_sell"
          productCode={productCode}
          isVisible={isFieldVisible}
        />
        {isMobile && isOB && <span />}
        {price != null && (
          <PriceButtonWithMark
            isAggressPrice
            isMarkOrBetter={isMarkOrBetter}
            disabled
            isLockedQuote
            formattedPrice={formattedPrice}
            price={price}
            side="SELL"
            productCode={productCode}
          />
        )}
        <ConfirmableButton
          onClick={onClick}
          status={status}
          disabled={!isButtonEnabled || isPlatformNotAvailable}
          side="SELL"
          data-testid={rfqId}
          aria-label="rfq_one_click_sell"
          rfqId={rfqId}
        />
      </Container>
    </RelativeContainer>
  );
}

interface ComfirmableButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  status: AggressAction['status'];
  side: Side;
  readonly rfqId: string | number;
}

function ConfirmableButton({
  status,
  side,
  rfqId,
  ...props
}: ComfirmableButtonProps) {
  const label = side === 'SELL' ? 'Sell' : 'Buy';
  const ariaLabel = `rfq_one_click_locked_price_${
    side === 'SELL' ? 'sell' : 'buy'
  }_confirm_button`;

  if (isConfirmState(status)) {
    return (
      <ConfirmButton
        {...props}
        data-testid={rfqId}
        aria-label={ariaLabel}
        css="padding: 0; margin: 0;"
      >
        {status === 'submitting' && <StyledLoadingSpinner alt="loading..." />}
        Confirm
      </ConfirmButton>
    );
  }

  return (
    <ActionButton ariaLabel={`quick ${label}`} {...props} side={side}>
      {label}
    </ActionButton>
  );
}

interface QuantityFieldProps
  extends Pick<NumberFormatProps, 'value' | 'disabled'> {
  readonly onChange: (value: string) => void;
  readonly decimalScale: number;
  readonly status: AggressAction['status'];
  readonly productCode?: string;
  readonly isVisible: boolean;
  readonly disabled: boolean;
  readonly side: Side;
}

function QuantityField({
  value,
  decimalScale,
  onChange,
  status,
  productCode,
  isVisible,
  side,
  disabled = false,
  ...props
}: QuantityFieldProps) {
  const isPlatformCreationDisabled = useUMPlatformCreationDisabled();
  const track = useDebouncedTrackAction();
  const handleChange = useCallback(
    (values: NumberFormatValues, sourceInfo: SourceInfo) => {
      onChange(values.value);
      if (sourceInfo.source === 'event') {
        track('change strategy quantity', { side });
      }
    },
    [onChange, side, track],
  );

  if (isConfirmState(status)) {
    return (
      <QuantityLabel>
        {formatProductQty(value as string, productCode, true)}
      </QuantityLabel>
    );
  }

  if (isVisible) {
    return (
      <StyledFormattedNumber
        {...props}
        status={status}
        onClick={(e) => e.stopPropagation()}
        className="agress-input-field"
        allowNegative={false}
        value={value}
        decimalScale={decimalScale}
        placeholder=""
        thousandSeparator
        onValueChange={handleChange}
        disabled={isPlatformCreationDisabled}
      />
    );
  }

  return null;
}

const isConfirmState = (status: AggressAction['status']) =>
  ['confirming', 'submitting'].includes(status);

const onQuoteSubmitted: SubmitCallback = (resp) => {
  if (!resp.ok) {
    toast(resp.message);
    return;
  }

  if (!resp.data.ok) {
    toastFailedQuote(resp.data);
  }
};

function toastFailedQuote(report: Report) {
  if (report.signal?.aborted === true) return;

  const { side, message } = report;
  const isBuySide = side === 'BUY';
  const type = isBuySide ? 'Bid Error' : 'Ask Error';
  toast(`${type}: ${message}`);
}

const StyledLoadingSpinner = styled(LoadingSpinner).attrs({ size: 15 })`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const QuantityLabel = styled.span`
  font-size: 12px;
  color: white;
  font-weight: bold;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 1rem;
  }
`;

const RelativeContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

export const Container = styled.div<{
  status: AggressAction['status'];
  side: Side;
}>`
  position: absolute;
  cursor: default;
  transition: background-color ease 0.2s;
  border-radius: 16px;
  height: 32px;
  display: inline-grid;
  grid-template-columns: auto auto;
  align-items: center;
  justify-content: center;
  column-gap: 6px;
  top: 0;
  bottom: 0;
  margin: auto;
  padding: 0 6px;

  ${({ status }) =>
    isConfirmState(status) &&
    css`
      background-color: #23374e;
      grid-template-columns: auto auto auto;
    `}

  ${({ side }) =>
    side === 'BUY'
      ? css`
          left: 0;
          border-bottom-left-radius: 0;
          border-top-left-radius: 0;
        `
      : css`
          right: 0;
          border-bottom-right-radius: 0;
          border-top-right-radius: 0;
        `}

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    justify-items: center;
    grid-template-columns: 7fr 1fr 8fr 3.5fr;
    grid-template-areas: 'bdesk . bid mark';
    gap: 0.25rem;

    ${({ side }) =>
      side === 'BUY'
        ? css`
            grid-template-columns: 3.5fr 8fr 1fr 7fr;
            grid-template-areas: 'mark bid . bdesk';
            left: 0;
            border-bottom-left-radius: 16px;
            border-top-left-radius: 16px;
          `
        : css`
            grid-template-columns: 7fr 1fr 8fr 3.5fr;
            grid-template-areas: 'bdesk . bid mark';
            right: 0;
            border-bottom-right-radius: 16px;
            border-top-right-radius: 16px;
          `}

    ${({ status, side }) =>
      isConfirmState(status) &&
      css`
        gap: 1rem;
        grid-template-columns: auto;
        position: absolute;
        width: 100vw;
        border-radius: 0;
        z-index: 1;
        right: ${side === 'BUY' ? '-1rem' : 'auto'};
        left: ${side === 'SELL' ? '-1rem' : 'auto'};
      `}
  }
`;

const StyledFormattedNumber = styled(FormattedNumber)<{
  status: AggressAction['status'];
}>`
  background-color: transparent;
  border: none;
  outline: none;
  padding: 0;
  color: white;
  opacity: 0.5;
  text-align: center;
  width: 100%;
  font-size: 12px;
  border-bottom: 1px solid transparent;
  transition: border-bottom ease 0.2s;
  transition: font-weight ease 0.2s;

  &:disabled {
    opacity: 0.2;
  }

  ${({ status }) =>
    isConfirmState(status) &&
    css`
      font-weight: bold;

      &:disabled {
        opacity: 1;
      }
    `}

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 15px;
  }
`;
